import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';
import { resolveKitBox } from './equipmentSilhouettePorts';
import type { FabProcessShellDescriptor } from './amhs/Equipment/FabProcessTool/fabProcessShellParts';

export type EquipmentKitWorldTransform = {
  x: number;
  y: number;
  z: number;
  rotationY: number;
};

/**
 * Standard Three.js Y-axis rotation of a local 3D offset around a center point.
 * [lx * c + lz * s, ly, -lx * s + lz * c]
 */
export function rotateYLocal(
  local: readonly [number, number, number] | [number, number, number],
  center: readonly [number, number, number] | [number, number, number],
  rotY: number
): [number, number, number] {
  const [lx, ly, lz] = local;
  const [cx, cy, cz] = center;
  if (Math.abs(rotY) < 1e-6) return [cx + lx, cy + ly, cz + lz];
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  return [cx + lx * c + lz * s, cy + ly, cz - lx * s + lz * c];
}

/**
 * Canonical world-space kit transform.
 *
 * Real equipment is placed at:
 *   position: [descriptor.originX, 0, descriptor.originY], rotation: [0, layoutRotY, 0]
 * with a child group at:
 *   position: [descriptor.width / 2, 0, descriptor.height / 2], rotation: [0, box.rotationY, 0].
 *
 * This function calculates the exact composed world transform so both real equipment
 * and instanced equipment derive from the exact same source transform.
 */
export function getEquipmentKitWorldTransform(
  descriptor: EquipmentRenderDescriptor
): EquipmentKitWorldTransform {
  const box = resolveKitBox(descriptor);
  const layoutRotY = (-(descriptor.rotationDeg || 0) * Math.PI) / 180;
  const hw = descriptor.width / 2;
  const hh = descriptor.height / 2;
  const cos = Math.cos(layoutRotY);
  const sin = Math.sin(layoutRotY);

  const x = descriptor.originX + hw * cos + hh * sin;
  const y = 0;
  const z = descriptor.originY - hw * sin + hh * cos;
  const rotationY = layoutRotY + box.rotationY;

  return { x, y, z, rotationY };
}

/** World-space kit anchor matching `EquipmentKitWorldGroup` / `PartBatch`. */
export function descriptorKitWorldCenter(descriptor: EquipmentRenderDescriptor): {
  x: number;
  y: number;
  z: number;
  rotationY: number;
} {
  return getEquipmentKitWorldTransform(descriptor);
}

export function descriptorToFabShellInstance(
  descriptor: EquipmentRenderDescriptor
): FabProcessShellDescriptor {
  const box = resolveKitBox(descriptor);
  const transform = getEquipmentKitWorldTransform(descriptor);
  return {
    position: [transform.x, transform.y, transform.z],
    rotation: [0, transform.rotationY, 0],
    width: box.w,
    depth: box.d,
    height: box.h,
  };
}

export function descriptorToCircleStockerShellInstance(descriptor: EquipmentRenderDescriptor): {
  position: [number, number, number];
  width: number;
  height: number;
  depth: number;
  rotation?: [number, number, number];
} {
  const box = resolveKitBox(descriptor);
  const transform = getEquipmentKitWorldTransform(descriptor);
  return {
    position: [transform.x, transform.y, transform.z],
    width: box.w,
    height: box.h,
    depth: box.d,
    rotation: [0, transform.rotationY, 0],
  };
}

export function descriptorLodCandidate(descriptor: EquipmentRenderDescriptor) {
  const transform = getEquipmentKitWorldTransform(descriptor);
  const box = resolveKitBox(descriptor);
  const worldSize = Math.max(box.w, box.h, box.d);
  return {
    id: descriptor.id,
    worldX: transform.x,
    worldY: worldSize * 0.5,
    worldZ: transform.z,
    worldSize,
  };
}
